import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Check, X, CheckSquare, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Aprobaciones() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [h, f, c] = await Promise.all([
        api.get("/horas", { params: { status_filter: "pendente" } }),
        api.get("/funcionarios"),
        api.get("/clientes"),
      ]);
      setItems(h.data);
      setFuncionarios(f.data);
      setClientes(c.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const approve = async (id, status) => {
    try {
      await api.put(`/horas/${id}/aprovar`, { status });
      toast.success(t("common.success"));
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const openEdit = (h) => {
    setEditing(h.id);
    setEditForm({
      hora_inicio: h.hora_inicio,
      hora_fim: h.hora_fim,
      almuerzo_horas: h.almuerzo_horas ?? 1.5,
      cliente_id: h.cliente_id || "",
      data: h.data,
      observacoes: h.observacoes || "",
    });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/horas/${editing}`, editForm);
      toast.success(t("common.success"));
      setEditing(null);
      setEditForm(null);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const getFuncName = (fid) => funcionarios.find((f) => f.id === fid)?.name || "—";
  const getClientName = (cid) => clientes.find((c) => c.id === cid)?.nome || "—";

  return (
    <div data-testid="aprobaciones-page">
      <div className="mb-8">
        <h1 className="text-3xl heading-font font-bold text-slate-900">{t("aprobaciones.title")}</h1>
        <p className="text-slate-500 mt-1 text-sm">{t("aprobaciones.subtitle")}</p>
      </div>

      {loading ? (
        <Card className="p-10 text-center text-slate-400">{t("common.loading")}</Card>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center text-slate-400 border-slate-200">
          <CheckSquare size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
          {t("aprobaciones.empty")}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((h) => (
            <Card key={h.id} className="p-5 border-slate-200 card-hover" data-testid={`approval-card-${h.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{h.data}</div>
                  <h3 className="heading-font font-semibold text-slate-900 mt-1">{getFuncName(h.funcionario_id)}</h3>
                </div>
                <Badge className="bg-amber-100 text-amber-800 border-0">{t("horas.pending")}</Badge>
              </div>

              <div className="grid grid-cols-4 gap-3 mb-3 pb-3 border-b border-slate-100">
                <div>
                  <div className="text-[11px] text-slate-500 uppercase">{t("horas.start")}</div>
                  <div className="text-sm font-semibold">{h.hora_inicio}</div>
                </div>
                <div>
                  <div className="text-[11px] text-slate-500 uppercase">{t("horas.end")}</div>
                  <div className="text-sm font-semibold">{h.hora_fim}</div>
                </div>
                <div>
                  <div className="text-[11px] text-slate-500 uppercase">{t("horas.lunch")}</div>
                  <div className="text-sm font-semibold">-{(h.almuerzo_horas ?? 1.5).toFixed(1)}h</div>
                </div>
                <div>
                  <div className="text-[11px] text-slate-500 uppercase">{t("horas.total")}</div>
                  <div className="text-sm font-bold text-green-700">{h.total_horas} h</div>
                </div>
              </div>

              <div className="text-sm text-slate-600 mb-3">
                <span className="text-slate-500">{t("horas.client")}: </span>
                <span className="font-medium text-slate-800">{getClientName(h.cliente_id)}</span>
              </div>
              {h.observacoes && <p className="text-xs text-slate-500 mb-3">{h.observacoes}</p>}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEdit(h)}
                  data-testid={`edit-approval-${h.id}`}
                  className="gap-1"
                >
                  <PencilSimple size={14} /> {t("aprobaciones.edit")}
                </Button>
                <Button className="flex-1 bg-green-600 hover:bg-green-700 gap-1" size="sm" onClick={() => approve(h.id, "aprovado")} data-testid={`approve-btn-${h.id}`}>
                  <Check size={14} weight="bold" /> {t("aprobaciones.approve")}
                </Button>
                <Button className="flex-1 bg-red-600 hover:bg-red-700 gap-1" size="sm" onClick={() => approve(h.id, "rejeitado")} data-testid={`reject-btn-${h.id}`}>
                  <X size={14} weight="bold" /> {t("aprobaciones.reject")}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) { setEditing(null); setEditForm(null); } }}>
        <DialogContent className="max-w-lg" data-testid="edit-horas-dialog">
          <DialogHeader>
            <DialogTitle>{t("aprobaciones.edit")}</DialogTitle>
          </DialogHeader>
          {editForm && (
            <div className="space-y-3">
              <div>
                <Label>{t("horas.date")}</Label>
                <Input type="date" value={editForm.data} onChange={(e) => setEditForm({ ...editForm, data: e.target.value })} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label>{t("horas.start")}</Label>
                  <Input type="time" value={editForm.hora_inicio} onChange={(e) => setEditForm({ ...editForm, hora_inicio: e.target.value })} />
                </div>
                <div>
                  <Label>{t("horas.end")}</Label>
                  <Input type="time" value={editForm.hora_fim} onChange={(e) => setEditForm({ ...editForm, hora_fim: e.target.value })} />
                </div>
                <div>
                  <Label>{t("horas.lunch")}</Label>
                  <Input type="number" step="0.1" min="0" value={editForm.almuerzo_horas} onChange={(e) => setEditForm({ ...editForm, almuerzo_horas: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>{t("horas.client")}</Label>
                <Select value={editForm.cliente_id} onValueChange={(v) => setEditForm({ ...editForm, cliente_id: v })}>
                  <SelectTrigger><SelectValue placeholder={t("horas.selectClient")} /></SelectTrigger>
                  <SelectContent>
                    {clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("horas.notes")}</Label>
                <Textarea value={editForm.observacoes} onChange={(e) => setEditForm({ ...editForm, observacoes: e.target.value })} rows={2} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>{t("common.cancel")}</Button>
            <Button onClick={saveEdit} className="bg-green-600 hover:bg-green-700" data-testid="save-edit-horas">{t("common.confirm")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
